/**
 * Colyseus client wrapper. One module-singleton room handle so different
 * React components can call into the same connection.
 *
 * URL strategy:
 *   - dev: ws://localhost:2569 (override via VITE_AIWARI_WS)
 *   - prod: wss://games.aiwari.com/aiwari-ws/ (nginx proxy)
 */

import { Client, getStateCallbacks, Room } from 'colyseus.js';
import { useNet, safeHpMax, safeResourceMax } from '../state/net-store.js';
import { useWorldBoss } from '../state/worldboss-store.js';
import { useMounts, wireMountServerMessages } from '../state/mount-store.js';
import { useGatherNodes } from '../state/gather-nodes-store.js';
import { getActiveRoom, setActiveRoom } from './activeRoom.js';

const DEV_WS = 'ws://localhost:2569';
const PROD_WS = `${location.protocol === 'https:' ? 'wss:' : 'ws:'}//${location.host}/aiwari-ws`;
const WS_URL = (import.meta.env.DEV ? DEV_WS : PROD_WS);

let client: Client | null = null;
let currentRoom: Room | null = null;
let lastJoinOpts: JoinOpts | null = null;
let reconnectTimer: number | null = null;
let reconnectAttempts = 0;
let manualDisconnect = false;
const ROOM_LISTENER_CLEANUP: Array<() => void> = [];

/**
 * R232q85: parallel mirror of `RealmState.sirusUi` MapSchema (sessionId →
 * { targetId, auras, activeCast }). The Sirus HUD fields used to live on
 * PlayerState (R232q81) but pushed it past the Colyseus 64-field cap; the
 * server now keeps them in a separate map under RealmState. We mirror them
 * here so `upsertPlayerFromState` can splice them into RemotePlayer at
 * upsert time without changing how the rest of the codebase reads
 * `players[id].targetId / auras / activeCast`.
 */
type SirusUiSnapshot = {
  targetId: string;
  auras: Array<{ id: string; icon: string; stacks: number; expiresAt: number; dispelType: string }>;
  activeCast: { abilityId: string; startTs: number; durationMs: number; channel: boolean; interrupted: boolean } | null;
};
type RemotePlayerSirusFields = {
  targetId: string;
  auras: SirusUiSnapshot['auras'];
  activeCast?: NonNullable<SirusUiSnapshot['activeCast']>;
};
const SIRUS_UI_BY_PLAYER = new Map<string, SirusUiSnapshot>();

/**
 * R232q85: when sirusUi changes for a player, re-emit the existing
 * RemotePlayer record into the store so consumers (TargetFrame / BuffBar /
 * CastBar) see the new flat fields. We don't have the underlying schema
 * `p` here — pull the latest `RemotePlayer` from net-store and merge.
 */
function rebroadcastSirusUiForPlayer(playerId: string): void {
  const cur = useNet.getState().players[playerId];
  if (!cur) return;
  const ui = SIRUS_UI_BY_PLAYER.get(playerId);
  useNet.getState().upsertPlayer({
    ...cur,
    targetId: ui?.targetId ?? '',
    auras: ui?.auras ? ui.auras.slice() : [],
    activeCast: ui?.activeCast ?? undefined,
  });
}

function snapshotSirusUi(s: any): SirusUiSnapshot {
  const auras: SirusUiSnapshot['auras'] = [];
  const m = s?.auras;
  if (m && typeof m.forEach === 'function') {
    m.forEach((a: { id?: string; icon?: string; stacks?: number; expiresAt?: number; dispelType?: string }) => {
      auras.push({
        id: a.id ?? '',
        icon: a.icon ?? '',
        stacks: a.stacks ?? 0,
        expiresAt: a.expiresAt ?? 0,
        dispelType: a.dispelType ?? '',
      });
    });
  }
  const ac = s?.activeCast;
  const activeCast = (ac && typeof ac.abilityId === 'string' && ac.abilityId.length > 0)
    ? {
        abilityId: ac.abilityId,
        startTs: ac.startTs ?? 0,
        durationMs: ac.durationMs ?? 0,
        channel: !!ac.channel,
        interrupted: !!ac.interrupted,
      }
    : null;
  return {
    targetId: typeof s?.targetId === 'string' ? s.targetId : '',
    auras,
    activeCast,
  };
}

export function getRoom(): Room | null {
  return currentRoom;
}

export interface JoinOpts {
  stormId: string;
  displayName: string;
  side: string;
  subFactionId: string;
  classId: string;
  /** R232q5r: realm shard. Если null/undefined — старый клиент,
   *  попадает в legacy 'realm' room (= eldran default).
   *  R232q5x: добавлен sharpcrest для соответствия RealmId в state/store. */
  realmId?: 'eldran' | 'grozovoy' | 'reserved-3' | 'sharpcrest';
  gender?: 'male' | 'female';
  bodyType?: 'slim' | 'medium' | 'broad';
  skinTone?: string;
  race?: string;
  faction?: string;
  hairStyle?: number;
  hairColour?: string;
}

export async function connectToRealm(opts: JoinOpts): Promise<Room> {
  if (!client) client = new Client(WS_URL);
  // R232q5h T2.5: clean up any leftover listeners from previous room before
  // rebinding. Without this, every reconnect doubled the handler count and
  // events fired N times → visual glitches, double damage etc.
  for (const off of ROOM_LISTENER_CLEANUP.splice(0)) {
    try { off(); } catch {}
  }
  // R232q85: drop any leftover sirusUi snapshots from a prior connection so
  // the new room starts with a clean per-player Sirus HUD mirror.
  SIRUS_UI_BY_PLAYER.clear();
  manualDisconnect = false;
  lastJoinOpts = opts;
  reconnectAttempts = 0;
  const net = useNet.getState();
  // R232q5r: per-realm room target. Если realmId передан — `realm-X`,
  // иначе legacy `realm` (server маппит в DEFAULT_REALM=eldran).
  const roomKey = opts.realmId ? `realm-${opts.realmId}` : 'realm';
  const room = await client.joinOrCreate(roomKey, opts);
  currentRoom = room;
  setActiveRoom(room);
  net.setSelf(room.sessionId);
  net.setConnected(true);
  // R232q5aN: hook mount-system server replies into client store.
  wireMountServerMessages(room as unknown as { onMessage: (k: string, fn: (data: unknown) => void) => void });

  // Colyseus 0.16+ moved schema callbacks behind getStateCallbacks().
  // The old `room.state.players.onAdd(...)` style silently no-ops and
  // the client never sees state updates — that's why HP/MP/XP read 0
  // and mobs were invisible.
  const $ = getStateCallbacks(room);
  $(room.state).players.onAdd((p, key) => {
    upsertPlayerFromState(p, key);
    $(p).onChange(() => upsertPlayerFromState(p, key));
    // R232q86zb4: nested PositionSchema doesn't bubble up to $(p).onChange.
    // Without this listener, server-side respawn (legacy-pos migration,
    // teleport, hearthstone, knockback, etc.) updates p.pos.x/z but the
    // net-store's players[id].pos stays whatever the initial onAdd had —
    // so the World render loop's snap-to-spawn never fires for late
    // server respawns.
    //
    // R232q86zdb (Wave 2 #2): nested pos onChange now uses the position-
    // only fast path. The full upsertPlayerFromState (which rebuilds buffs/
    // learnedAbilities/knownSpells/glyphSlots arrays + slices auras) used
    // to fire ~15Hz × 60 players × ~24 allocations = ~21,600 GC objects/s
    // just to keep pos in sync. The fast path mutates pos in place and
    // returns a fresh players map without touching the array fields.
    if (p.pos && $(p.pos).onChange) {
      $(p.pos).onChange(() => {
        const x = typeof p.pos?.x === 'number' ? p.pos.x : 0;
        const y = typeof p.pos?.y === 'number' ? p.pos.y : 0;
        const z = typeof p.pos?.z === 'number' ? p.pos.z : 0;
        const heading = typeof p.heading === 'number' ? p.heading : 0;
        const moving = typeof p.moving === 'boolean' ? p.moving : undefined;
        useNet.getState().upsertPlayerPosition(key, x, y, z, heading, moving);
      });
    }
  });
  $(room.state).players.onRemove((_p, key) => {
    useNet.getState().removePlayer(key);
    // R232q85: also drop the matching sirusUi snapshot. Server's onLeave
    // already deletes the row, but if onRemove fires first we want the local
    // mirror to follow immediately so a re-add picks up a clean snapshot.
    SIRUS_UI_BY_PLAYER.delete(key);
  });

  // R232q85: RealmState.sirusUi (sessionId → SirusUiSchema) — Sirus HUD
  // replication for targetId / auras / activeCast lives here instead of on
  // PlayerState (which hit the 64-field cap). On every change we refresh
  // the parallel mirror and re-upsert the matching RemotePlayer so the
  // flat `targetId / auras / activeCast` fields stay in sync.
  ($(room.state) as { sirusUi?: {
    onAdd?: (cb: (s: any, key: string) => void) => void;
    onRemove?: (cb: (s: any, key: string) => void) => void;
  } }).sirusUi?.onAdd?.((s: any, key: string) => {
    SIRUS_UI_BY_PLAYER.set(key, snapshotSirusUi(s));
    rebroadcastSirusUiForPlayer(key);
    // Sub-fields fire onChange independently — listen to the SirusUiSchema
    // itself plus its `auras` MapSchema and `activeCast` sub-schema.
    $(s).onChange?.(() => {
      SIRUS_UI_BY_PLAYER.set(key, snapshotSirusUi(s));
      rebroadcastSirusUiForPlayer(key);
    });
    $(s).auras?.onAdd?.(() => {
      SIRUS_UI_BY_PLAYER.set(key, snapshotSirusUi(s));
      rebroadcastSirusUiForPlayer(key);
    });
    $(s).auras?.onRemove?.(() => {
      SIRUS_UI_BY_PLAYER.set(key, snapshotSirusUi(s));
      rebroadcastSirusUiForPlayer(key);
    });
    if (s.activeCast) {
      $(s.activeCast).onChange?.(() => {
        SIRUS_UI_BY_PLAYER.set(key, snapshotSirusUi(s));
        rebroadcastSirusUiForPlayer(key);
      });
    }
  });
  ($(room.state) as { sirusUi?: { onRemove?: (cb: (s: any, key: string) => void) => void } }).sirusUi?.onRemove?.((_s: any, key: string) => {
    SIRUS_UI_BY_PLAYER.delete(key);
    rebroadcastSirusUiForPlayer(key);
  });

  $(room.state).mobs.onAdd((m, key) => {
    upsertMobFromState(m, key);
    $(m).onChange(() => upsertMobFromState(m, key));
  });
  $(room.state).mobs.onRemove((_m, key) => {
    useNet.getState().removeMob(key);
  });

  // R232q5s: server NpcRegistry → net-store.npcs sync.
  $(room.state).npcs?.onAdd?.((n, key: string) => {
    upsertNpcFromState(n, key);
    $(n).onChange(() => upsertNpcFromState(n, key));
  });
  $(room.state).npcs?.onRemove?.((_n, key: string) => {
    useNet.getState().removeNpc(key);
  });

  // R232q5aO (Wave 3a #7): Combat pets → net-store.pets sync.
  $(room.state).pets?.onAdd?.((pet: any, key: string) => {
    upsertPetFromState(pet, key);
    $(pet).onChange(() => upsertPetFromState(pet, key));
  });
  $(room.state).pets?.onRemove?.((_p: any, key: string) => {
    useNet.getState().removePet(key);
  });

  // R232q5aP-spawn: gather nodes (mining veins / herb patches) — server
  // GatherNodeManager populates state.gatherNodes per zone on boot.
  // Client mirrors into useGatherNodes store; scene/GatherNodeMeshes
  // renders a marker mesh at each pos and the click handler in World.tsx
  // sends the `gather-node` opcode for skill validation.
  useGatherNodes.getState().clear();
  $(room.state).gatherNodes?.onAdd?.((g: any, key: string) => {
    upsertGatherNodeFromState(g, key);
    $(g).onChange(() => upsertGatherNodeFromState(g, key));
  });
  $(room.state).gatherNodes?.onRemove?.((_g: any, key: string) => {
    useGatherNodes.getState().remove(key);
  });

  $(room.state).listen('tidalCount', (v: number) => {
    const cur = useNet.getState().factionCounts;
    useNet.getState().setFactionCounts({ ...cur, tidal: v });
  });
  $(room.state).listen('rootedCount', (v: number) => {
    const cur = useNet.getState().factionCounts;
    useNet.getState().setFactionCounts({ ...cur, rooted: v });
  });
  $(room.state).listen('anchorlessCount', (v: number) => {
    const cur = useNet.getState().factionCounts;
    useNet.getState().setFactionCounts({ ...cur, anchorless: v });
  });
  $(room.state).listen('tidalStormBonus', (v: number) => {
    const cur = useNet.getState().stormBonuses;
    useNet.getState().setStormBonuses({ ...cur, tidal: v });
  });
  $(room.state).listen('rootedStormBonus', (v: number) => {
    const cur = useNet.getState().stormBonuses;
    useNet.getState().setStormBonuses({ ...cur, rooted: v });
  });
  $(room.state).listen('anchorlessStormBonus', (v: number) => {
    const cur = useNet.getState().stormBonuses;
    useNet.getState().setStormBonuses({ ...cur, anchorless: v });
  });

  // R232q20-Sirus-wire: group/raid composition + raidMode listeners.
  // The server stubs these as empty maps until the party-system pass
  // lands; the listeners below stay wired so the moment the server
  // populates the maps the GroupFrame / RaidFrame go live without a
  // client redeploy. Each listener rebuilds the plain Record from the
  // MapSchema on every change (cheap — typical raid ≤40 entries).
  const flattenStringMap = (m: { forEach?: (cb: (v: string, k: string) => void) => void } | undefined): Record<string, string> => {
    const out: Record<string, string> = {};
    if (m && typeof m.forEach === 'function') m.forEach((v, k) => { out[k] = v; });
    return out;
  };
  // Colyseus 0.16 fires .listen on string-valued primitives only — for
  // MapSchema we must subscribe via the schema callbacks API. Defensive
  // optional chains so older snapshots without these fields don't throw.
  ($(room.state) as { groupComposition?: { onAdd?: (cb: () => void) => void; onRemove?: (cb: () => void) => void; onChange?: (cb: () => void) => void } }).groupComposition?.onAdd?.(() => {
    useNet.getState().setGroupComposition(flattenStringMap((room.state as unknown as { groupComposition?: { forEach?: (cb: (v: string, k: string) => void) => void } }).groupComposition));
  });
  ($(room.state) as { groupComposition?: { onRemove?: (cb: () => void) => void } }).groupComposition?.onRemove?.(() => {
    useNet.getState().setGroupComposition(flattenStringMap((room.state as unknown as { groupComposition?: { forEach?: (cb: (v: string, k: string) => void) => void } }).groupComposition));
  });
  ($(room.state) as { raidComposition?: { onAdd?: (cb: () => void) => void; onRemove?: (cb: () => void) => void } }).raidComposition?.onAdd?.(() => {
    useNet.getState().setRaidComposition(flattenStringMap((room.state as unknown as { raidComposition?: { forEach?: (cb: (v: string, k: string) => void) => void } }).raidComposition));
  });
  ($(room.state) as { raidComposition?: { onRemove?: (cb: () => void) => void } }).raidComposition?.onRemove?.(() => {
    useNet.getState().setRaidComposition(flattenStringMap((room.state as unknown as { raidComposition?: { forEach?: (cb: (v: string, k: string) => void) => void } }).raidComposition));
  });
  $(room.state).listen('raidMode', (v: boolean) => {
    useNet.getState().setRaidMode(!!v);
  });

  // R232q86zb5: server tells the client where this player should spawn —
  // bypasses the unreliable nested PositionSchema decode. Client dispatches
  // a window event that World.tsx listens for to snap the visual rig.
  room.onMessage('self-pos', (msg: { x: number; y: number; z: number; zoneId?: string }) => {
    try {
      window.dispatchEvent(new CustomEvent('aiwari-self-pos', { detail: msg }));
      console.log(`[realmClient] self-pos (${Math.round(msg.x)}, ${Math.round(msg.z)}) zone=${msg.zoneId ?? '?'}`);
    } catch {}
  });

  // R232q5r-hardcore: окончательная смерть на Бессмертнике. Сервер шлёт
  // это после kill (через ~3.5с force-leave). Клиент показывает wax-seal
  // экран Архива Павших и сбрасывает stage в character-roster.
  room.onMessage('permadead', (msg: {
    name: string; level: number; zone: string; killer: string; ts: number;
  }) => {
    console.warn('[realmClient] permadeath:', msg);
    try {
      sessionStorage.setItem('aiwari-permadeath', JSON.stringify(msg));
      window.dispatchEvent(new CustomEvent('aiwari-permadeath', { detail: msg }));
    } catch {}
  });

  // Login отказ — character уже погиб ранее. UI должен закрыть login flow
  // и предложить создать нового character'а на этом realm'е.
  room.onMessage('permadead-locked', (msg: {
    name: string; level: number; zone: string; killer: string; permadead_at: string | null;
  }) => {
    console.warn('[realmClient] login refused (permadead):', msg);
    try {
      sessionStorage.setItem('aiwari-permadeath-locked', JSON.stringify(msg));
      window.dispatchEvent(new CustomEvent('aiwari-permadead-locked', { detail: msg }));
    } catch {}
  });

  // Realm mismatch — character принадлежит другому шарду.
  room.onMessage('realm-mismatch', (msg: { expected: string; current: string }) => {
    console.warn('[realmClient] realm mismatch:', msg);
    try {
      window.dispatchEvent(new CustomEvent('aiwari-realm-mismatch', { detail: msg }));
    } catch {}
  });

  room.onMessage('world-boss-event', (msg: {
    kind: 'spawn' | 'killed';
    bossId: string;
    name: string;
    pos?: { x: number; z: number };
    zone?: string;
    topDamageBy?: string;
    participants?: number;
    totalDamage?: number;
    nextRespawnAt?: number;
    ts: number;
  }) => {
    useWorldBoss.getState().pushEvent(msg);
  });

  room.onLeave(() => {
    net.setConnected(false);
    currentRoom = null;
    // R232q5h T2.4: auto-reconnect after unintentional disconnect.
    // Backoff: 1s, 2s, 4s, 8s, 16s, 30s cap. Up to 6 attempts (~60s total).
    if (!manualDisconnect && lastJoinOpts) {
      scheduleReconnect();
    }
  });

  return room;
}

function scheduleReconnect(): void {
  if (reconnectTimer != null) return;
  if (reconnectAttempts >= 6) {
    console.warn('[realmClient] reconnect: max attempts reached, giving up');
    return;
  }
  const delays = [1000, 2000, 4000, 8000, 16000, 30000];
  const delay = delays[Math.min(reconnectAttempts, delays.length - 1)]!;
  reconnectAttempts += 1;
  console.log(`[realmClient] scheduling reconnect attempt ${reconnectAttempts} in ${delay}ms`);
  reconnectTimer = window.setTimeout(async () => {
    reconnectTimer = null;
    if (!lastJoinOpts) return;
    try {
      await connectToRealm(lastJoinOpts);
      console.log('[realmClient] reconnect success');
    } catch (err) {
      console.warn('[realmClient] reconnect failed:', err);
      scheduleReconnect();
    }
  }, delay);
}

/**
 * R232q5h T2.5: register a listener cleanup so reconnect can purge them
 * before re-binding to the new room. Components that subscribe to room
 * messages should pass their off-fn here.
 */
export function registerRoomListenerCleanup(off: () => void): void {
  ROOM_LISTENER_CLEANUP.push(off);
}

function upsertPlayerFromState(p: any, key: string) {
  // R232q5ah: derive defensive HP/resource caps from class+level when the
  // schema fields arrive as 0 or undefined. The server is authoritative,
  // but a transient onAdd before recomputePlayerStats has propagated can
  // leave hpMax = schema-default 0 → HUD rendered "148/1" via `?? 1`.
  // Falling back to baseline keeps the bar correct until the real value
  // streams in (typically within one tick).
  const classId = p.classId ?? 'warrior';
  const level = p.level ?? 1;
  const hpMax = safeHpMax(p.hpMax, classId, level);
  const resourceMax = safeResourceMax(p.resourceMax, classId, level);
  const rawHp = p.hp;
  const rawRes = p.resource;
  const hp = (typeof rawHp === 'number' && rawHp >= 0) ? rawHp : hpMax;
  const resource = (typeof rawRes === 'number' && rawRes >= 0) ? rawRes : resourceMax;
  useNet.getState().upsertPlayer({
    id: key,
    name: p.displayName ?? '',
    side: p.side ?? '',
    classId,
    level,
    hp,
    hpMax,
    resource,
    resourceMax,
    pow: p.pow ?? 0,
    armor: p.armor ?? 0,
    xp: p.xp ?? 0,
    specId: p.specId ?? '',
    critPct: p.critPct ?? 5,
    hastePct: p.hastePct ?? 0,
    masteryPct: p.masteryPct ?? 0,
    versPct: p.versPct ?? 0,
    pos: { x: p.pos?.x ?? 0, y: p.pos?.y ?? 0, z: p.pos?.z ?? 0 },
    heading: p.heading ?? 0,
    moving: !!p.moving,
    zoneId: p.zoneId ?? '',
    realmKills: p.realmKills ?? 0,
    realmDeaths: p.realmDeaths ?? 0,
    realmPoints: p.realmPoints ?? 0,
    dead: !!p.dead,
    corpsePos: { x: p.corpsePos?.x ?? 0, z: p.corpsePos?.z ?? 0 },
    // R160: server fields для buff strip / mount mesh / SC counter.
    buffs: (() => {
      const out: Array<{ id: string; endsAt: number }> = [];
      if (p.buffs && typeof p.buffs.forEach === 'function') {
        p.buffs.forEach((b: { id: string; endsAt: number }) => out.push({ id: b.id, endsAt: b.endsAt }));
      }
      return out;
    })(),
    mounted: !!p.mounted,
    mountId: p.mountId ?? '',
    stormCrystals: p.stormCrystals ?? 0,
    learnedAbilities: (() => {
      const out: string[] = [];
      if (p.learnedAbilities && typeof p.learnedAbilities.forEach === 'function') {
        p.learnedAbilities.forEach((s: string) => out.push(s));
      }
      return out;
    })(),
    // R232q44: numeric WotLK DBC spell ids — drives Spellbook «Заклинания» tab.
    knownSpells: (() => {
      const out: number[] = [];
      if (p.knownSpells && typeof p.knownSpells.forEach === 'function') {
        p.knownSpells.forEach((id: number) => { if (typeof id === 'number') out.push(id); });
      }
      return out;
    })(),
    // R232q44: 6 glyph slot ids (0 = empty). Major 0-2, Minor 3-5.
    glyphSlots: (() => {
      const out: number[] = [];
      if (p.glyphSlots && typeof p.glyphSlots.forEach === 'function') {
        p.glyphSlots.forEach((id: number) => { if (typeof id === 'number') out.push(id); });
      }
      return out;
    })(),
    // R232q85: Sirus HUD данные живут в RealmState.sirusUi MapSchema (а не
    // на PlayerState — он на 64-field cap Colyseus). Читаем из параллельного
    // mirror SIRUS_UI_BY_PLAYER (заполняется sirusUi listener выше).
    ...(() => {
      const ui = SIRUS_UI_BY_PLAYER.get(key);
      if (!ui) return { targetId: '', auras: [] as RemotePlayerSirusFields['auras'], activeCast: undefined };
      return {
        targetId: ui.targetId,
        auras: ui.auras.slice(),
        activeCast: ui.activeCast ?? undefined,
      };
    })(),
  });
  // R232q5aN: replicate ridingSkill + CWF into client mount-store, but
  // ONLY for self — remote players don't need their own skill mirrored.
  if (key === useNet.getState().selfId) {
    const m = useMounts.getState();
    const newSkill = typeof p.ridingSkill === 'number' ? p.ridingSkill : 0;
    if (m.ridingSkill !== newSkill) m.setRidingSkill(newSkill);
    const cwf = !!p.coldWeatherFlying;
    if (m.coldWeatherFlying !== cwf) m.setColdWeatherFlying(cwf);
  }
}

function upsertNpcFromState(n: any, key: string) {
  useNet.getState().upsertNpc({
    id: key,
    displayName: n.displayName ?? '',
    subtitle: n.subtitle ?? '',
    npcType: n.npcType ?? 'questGiver',
    faction: n.faction ?? 'neutral',
    zoneId: n.zoneId ?? '',
    modelRef: n.modelRef ?? '',
    pos: { x: n.pos?.x ?? 0, y: n.pos?.y ?? 0, z: n.pos?.z ?? 0 },
    heading: n.heading ?? 0,
    questMarker: n.questMarker ?? '',
    lastEmote: n.lastEmote ?? '',
    lastEmoteAt: n.lastEmoteAt ?? 0,
    tags: n.tags ?? '',
  });
}

function upsertPetFromState(p: any, key: string) {
  const abilities: string[] = [];
  if (p.abilities && typeof p.abilities.forEach === 'function') {
    p.abilities.forEach((a: string) => abilities.push(a));
  }
  const cooldowns: Record<string, number> = {};
  if (p.cooldowns && typeof p.cooldowns.forEach === 'function') {
    p.cooldowns.forEach((v: number, k: string) => { cooldowns[k] = v; });
  }
  useNet.getState().upsertPet({
    id: key,
    ownerId: p.ownerId ?? '',
    petType: p.petType ?? '',
    displayName: p.displayName ?? '',
    family: p.family ?? 0,
    level: p.level ?? 1,
    xp: p.xp ?? 0,
    hp: p.hp ?? 0,
    hpMax: p.hpMax ?? 1,
    mana: p.mana ?? 0,
    manaMax: p.manaMax ?? 0,
    attackPower: p.attackPower ?? 0,
    armor: p.armor ?? 0,
    pos: { x: p.pos?.x ?? 0, y: p.pos?.y ?? 0, z: p.pos?.z ?? 0 },
    heading: p.heading ?? 0,
    stance: p.stance ?? 'follow',
    target: p.target ?? '',
    abilities,
    cooldowns,
    modelRef: p.modelRef ?? '',
  });
}

function upsertGatherNodeFromState(g: any, key: string) {
  useGatherNodes.getState().upsert({
    id: key,
    kind: g.kind ?? '',
    professionId: g.professionId ?? '',
    minSkill: g.minSkill ?? 0,
    yellow: g.yellow ?? 0,
    green: g.green ?? 0,
    grey: g.grey ?? 0,
    pos: { x: g.pos?.x ?? 0, y: g.pos?.y ?? 0, z: g.pos?.z ?? 0 },
    depletedAt: g.depletedAt ?? 0,
    respawnSecs: g.respawnSecs ?? 60,
    zoneId: g.zoneId ?? '',
  });
}

function upsertMobFromState(m: any, key: string) {
  // Schema arrays look like ArraySchema — we copy items into a plain array.
  const lootArr: string[] = [];
  if (m.loot && typeof m.loot.forEach === 'function') {
    m.loot.forEach((s: string) => lootArr.push(s));
  } else if (Array.isArray(m.loot)) {
    for (const s of m.loot) lootArr.push(s);
  }
  useNet.getState().upsertMob({
    id: key,
    kind: m.kind ?? 'wolf',
    level: m.level ?? 1,
    hp: m.hp ?? 0,
    hpMax: m.hpMax ?? 1,
    pos: { x: m.pos?.x ?? 0, y: m.pos?.y ?? 0, z: m.pos?.z ?? 0 },
    heading: m.heading ?? 0,
    dead: !!m.dead,
    loot: lootArr,
    despawnAt: m.despawnAt ?? 0,
    // R232q5ao: forward server aggro target so World.tsx distance-cull keeps
    // mobs visible while they're chasing the player (was: missing → mobs >60 m
    // chasing me became invisible, then leashed back, looking like "стоят").
    target: typeof m.target === 'string' ? m.target : '',
    // R232q86zbj: Schema MobState has displayName (server sets via
    // polishedNameForEn / russifyMobName). Forward into client store so
    // mob nameplates show real Russian name instead of generic kind label.
    displayName: typeof m.displayName === 'string' ? m.displayName : '',
    // R232q86zh6: canonical CreatureDisplayInfo id → client model+skin.
    displayId: typeof m.displayId === 'number' ? m.displayId : 0,
  });
}

export function sendMove(vx: number, vz: number, heading: number, running: boolean) {
  getActiveRoom()?.send('move', { vx, vz, heading, running });
}

export function sendAttack(mobId: string) {
  getActiveRoom()?.send('attack', { mobId });
}

/**
 * R232q20-Sirus-wire: dispatch a target change to the server so
 * `player.targetId` replicates to every observer (party assists,
 * focus frames, target-of-target). Pass `null` or empty string to
 * clear. Safe to call before connection — silently no-ops.
 */
export function sendSetTarget(id: string | null | undefined): void {
  getActiveRoom()?.send('setTarget', { id: id ?? '' });
}

/** R232q5aP-spawn: ask the server to gather a node (mining vein / herb patch).
 *  Server validates proximity (<6m), profession skill, and cooldown then
 *  responds with 'gather-node-result' { ok, items, newSkill, error }. */
export function sendGatherNode(nodeId: string) {
  getActiveRoom()?.send('gather-node', { nodeId });
}

// ── R232q5aO (Wave 3a #7): Combat pet client API ──
export function sendTameBeastStart(mobId: string) {
  getActiveRoom()?.send('tame-beast-start', { mobId });
}
export function sendTameBeastCancel() {
  getActiveRoom()?.send('tame-beast-cancel', {});
}
export function sendSummonPet(petType: string) {
  getActiveRoom()?.send('summon-pet', { petType });
}
export function sendPetCommand(command: 'attack' | 'follow' | 'stay' | 'passive' | 'dismiss' | 'cast', opts?: { abilityId?: string; targetId?: string }) {
  getActiveRoom()?.send('pet-command', { command, abilityId: opts?.abilityId, targetId: opts?.targetId });
}
export function sendStableList() { getActiveRoom()?.send('stable-list', {}); }
export function sendStableStore() { getActiveRoom()?.send('stable-store', {}); }
export function sendStableCall(index: number) {
  getActiveRoom()?.send('stable-call', { index });
}

export function sendAbility(abilityId: string, targetId?: string) {
  getActiveRoom()?.send('ability', { abilityId, targetId });
}

export function sendTalentSpec(specId: string) {
  getActiveRoom()?.send('talent.spec', { specId });
}

export function sendTalentStats(stats: {
  crit?: number; haste?: number; dmg?: number; armor?: number;
  hp?: number; mastery?: number; vers?: number;
}) {
  getActiveRoom()?.send('talent-stats', stats);
}

/**
 * R232q5h T2.3: persist the full talent allocation map server-side so it
 * survives cross-device login. Sent by TalentTree on every invest.
 */
export function sendTalentAllocations(allocations: Record<string, number>) {
  getActiveRoom()?.send('talent-allocations', { allocations });
}

export function sendCastWotlk(wowId: number, targetId?: string) {
  getActiveRoom()?.send('cast-wotlk', { wowId, targetId });
}
// Backward compatibility alias — older call sites may still import sendCastVanilla.
export const sendCastVanilla = sendCastWotlk;

export function sendChat(channel: 'say' | 'zone' | 'faction' | 'whisper' | 'guild', text: string, targetId?: string) {
  getActiveRoom()?.send('chat', { channel, text, targetId });
}

/** R232q5s: extended chat router (server ChatRouter pipeline). Six canonical
 *  channels: say / yell / whisper / party / guild / realm. */
export type ExtendedChatChannel = 'say' | 'yell' | 'whisper' | 'party' | 'guild' | 'realm';
export function sendChatExtended(channel: ExtendedChatChannel, body: string, targetName?: string) {
  getActiveRoom()?.send('chat-send', { channel, body, targetName });
}

export interface ExtendedChatMessage {
  id: string;
  senderId: string;
  senderName: string;
  senderFaction: string;
  channel: ExtendedChatChannel | 'system';
  channelId: string | null;
  body: string;
  ts: number;
}

export function onChatExtended(handler: (msg: ExtendedChatMessage) => void): () => void {
  const room = currentRoom;
  if (!room) return () => {};
  const cb = (msg: ExtendedChatMessage) => handler(msg);
  room.onMessage('chat-msg', cb);
  return () => { /* Colyseus 0.16 has no offMessage; lifecycle bound to room */ };
}

export function sendPartyInvite(targetId: string) {
  getActiveRoom()?.send('party-invite', { targetId });
}

export function onChat(handler: (msg: ChatMessage) => void): () => void {
  const room = currentRoom;
  if (!room) return () => {};
  const cb = (msg: ChatMessage) => handler(msg);
  room.onMessage('chat', cb);
  return () => {
    // Colyseus 0.16 doesn't expose offMessage cleanly; we leave the handler
    // wired for the lifetime of the room (room.leave on disconnect cleans up).
  };
}

export interface ChatMessage {
  from: string;
  fromName: string;
  fromSide: string;
  fromZone: string;
  channel: 'say' | 'zone' | 'faction' | 'whisper';
  targetId: string | null;
  text: string;
  ts: number;
}

export function disconnect() {
  manualDisconnect = true;
  if (reconnectTimer != null) { clearTimeout(reconnectTimer); reconnectTimer = null; }
  for (const off of ROOM_LISTENER_CLEANUP.splice(0)) {
    try { off(); } catch {}
  }
  currentRoom?.leave();
  if (getActiveRoom() === currentRoom) setActiveRoom(null);
  currentRoom = null;
  useNet.getState().setConnected(false);
}
